import {
  Button,
  Drawer,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel
} from "@mui/material";
import { COUNTRY_CODE_MAP } from "../../../../constants/constant";

const FilterDrawer = ({
  open,
  onClose,
  filters,
  volumeOptions,
  newReasonCodeOptions,
  countryOptions,
  monthOptions,
  yearOptions,
  handleFilterChange,
  applyFilters,
  clearFilters,
  areAllFiltersValid,
  isApplyButtonEnabled
}) => {
  const handleApplyFilters = () => {
    const success = applyFilters();
    if (success) {
      onClose();
    }
  };

  // Debug the apply button state
  const applyButtonEnabled = isApplyButtonEnabled();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 350,
          p: 3,
        },
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h6">Advanced Filters</Typography>

        {/* Volume - Radio Buttons */}
        <FormControl component="fieldset">
          <FormLabel component="legend">Volume</FormLabel>
          <RadioGroup
            value={filters.volume}
            onChange={(e) => handleFilterChange('volume', e.target.value)}
            row
          >
            {volumeOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* New Reason Code - Single Select */}
        <FormControl fullWidth>
          <InputLabel>New Reason Code</InputLabel>
          <Select
            value={filters.newReasonCode || ''}
            onChange={(e) => handleFilterChange('newReasonCode', e.target.value)}
            label="New Reason Code"
          >
            {newReasonCodeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>



        {/* Country - Single Select */}
        <FormControl fullWidth>
          <InputLabel>Country</InputLabel>
          <Select
            value={filters.country}
            onChange={(e) => handleFilterChange('country', e.target.value)}
            label="Country"
          >
            {countryOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {COUNTRY_CODE_MAP[option] || option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Month and Year Selection */}
        <Stack spacing={2}>
          <Typography variant="subtitle2">Time Range</Typography>
          
          {/* Start Range - Year and Month together */}
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Start Range</Typography>
          <Stack direction="row" spacing={1}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={filters.startYear}
                onChange={(e) => handleFilterChange('startYear', e.target.value)}
                label="Year"
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={filters.startMonth || ''}
                onChange={(e) => handleFilterChange('startMonth', e.target.value || null)}
                label="Month"
              >
                {monthOptions.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* End Range - Year and Month together */}
          <Typography variant="body2" sx={{ fontWeight: 'medium', mt: 2 }}>End Range</Typography>
          <Stack direction="row" spacing={1}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={filters.endYear}
                onChange={(e) => handleFilterChange('endYear', e.target.value)}
                label="Year"
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={filters.endMonth || ''}
                onChange={(e) => handleFilterChange('endMonth', e.target.value || null)}
                label="Month"
              >
                {monthOptions.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button 
            variant="contained" 
            onClick={handleApplyFilters} 
            fullWidth
            disabled={!applyButtonEnabled}
            sx={{
              backgroundColor: applyButtonEnabled ? '#ffde00' : '#e0e0e0', // Theme Yellow when enabled, gray when disabled
              color: applyButtonEnabled ? '#1b1c1b' : '#9e9e9e', // Dark text when enabled, gray when disabled
              '&:hover': {
                backgroundColor: applyButtonEnabled ? '#dda002' : '#e0e0e0', // Yellow 75 for hover state when enabled
              },
              '&.Mui-disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            Apply Filters
          </Button>
          <Button variant="outlined" onClick={clearFilters} fullWidth>
            Clear All
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default FilterDrawer;